import { useEffect } from "react";

import { areReferencedNodeValuesEqual } from "@doitintl/cloudflow-commons";
import { ModelType, NodeTransformationType, type ReferencedNodeValue } from "@doitintl/cmp-models";
import { Stack, TextField } from "@mui/material";
import { useFormikContext } from "formik";
import * as yup from "yup";

import { cloudflowTexts } from "../../../../../../assets/texts";
import { ReferencedFieldStandalone } from "../../../ApiActionParametersForm/parameters/wrappers/ReferencedField/ReferencedFieldStandalone";
import { type NodeWitOutputModel } from "../../../ApiActionParametersForm/parameters/wrappers/ReferencedField/useReferencedFieldContext";
import { createReferencedNodeSchema } from "../../../ApiActionParametersForm/useApiActionParametersSchema";
import { useFieldCommonProps } from "../../../ApiActionParametersForm/useFieldCommonProps";
import { FieldSectionHeader } from "../../../Common/FieldSectionHeader";
import { useTransformationNodeSchemaContext } from "./TransformationNodeForm";

const newFieldNameLabel = cloudflowTexts.NEW_FIELD_NAME_LABEL;

function generateAverageTransformSchema(
  referenceableNodes: NodeWitOutputModel[],
  rootReferencedNodeValue: ReferencedNodeValue = {
    referencedField: [],
    referencedNodeId: "",
  }
) {
  return yup.object({
    type: yup.string().default(NodeTransformationType.AVERAGE),
    newFieldName: yup.string().default("").required().label(newFieldNameLabel).trim(),
    payload: createReferencedNodeSchema(referenceableNodes, {
      requiredModelType: (referencedModel) =>
        referencedModel.type === ModelType.INTEGER ||
        referencedModel.type === ModelType.FLOAT ||
        (referencedModel.type === ModelType.LIST &&
          (referencedModel.member.model.type === ModelType.INTEGER ||
            referencedModel.member.model.type === ModelType.FLOAT)),
    })
      .label("Referenced value")
      .test(
        "referenced-field-not-equal-to-itself",
        "Referenced value must not be equal to itself",
        (value) => !areReferencedNodeValuesEqual(value, rootReferencedNodeValue)
      ),
  });
}

export const AverageTransformationForm = () => {
  const { setTransformationSchema, referenceableNodes } = useTransformationNodeSchemaContext();
  const { getFieldProps, setFieldValue } = useFormikContext();

  const { value: rootReferencedNodeValue } = getFieldProps("referencedNodeField");
  const { value: transformationFieldValue } = getFieldProps("transformation");

  useEffect(() => {
    const validationSchema = generateAverageTransformSchema(referenceableNodes, rootReferencedNodeValue);
    setTransformationSchema(validationSchema);

    if (!transformationFieldValue) {
      setFieldValue("transformation", validationSchema.getDefault());
    }

    // NOTE: formik does not support callback version of setFieldValue, it has been requested but the project is
    // abandoned, ignoring 'transformationFieldValue' here instead
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceableNodes, rootReferencedNodeValue, setTransformationSchema, setFieldValue]);

  const dataSourceNodes = referenceableNodes.filter(({ id }) => rootReferencedNodeValue.referencedNodeId === id);

  const newFieldNameCommonProps = useFieldCommonProps(
    getFieldProps("transformation.newFieldName"),
    newFieldNameLabel,
    true
  );
  const payloadCommonProps = useFieldCommonProps(
    getFieldProps("transformation.payload"),
    cloudflowTexts.REFERENCED_VALUE_LABEL,
    true
  );

  const isReferencedFieldDisabled = dataSourceNodes.length === 0;

  return (
    <Stack
      spacing={2}
      sx={{
        pb: 4,
      }}
    >
      <TextField fullWidth variant="outlined" size="small" {...newFieldNameCommonProps} />
      <FieldSectionHeader
        title={cloudflowTexts.AVERAGE_TRANSFORMATION_TITLE}
        subtitle={cloudflowTexts.AVERAGE_TRANSFORMATION_SUBTITLE}
      />
      <ReferencedFieldStandalone
        {...payloadCommonProps}
        referenceableNodes={dataSourceNodes}
        disabled={isReferencedFieldDisabled}
        tooltip={isReferencedFieldDisabled ? cloudflowTexts.SELECT_VALUE_FROM_DATA_SOURCE : ""}
        rootReferencedNodeValue={rootReferencedNodeValue}
      />
    </Stack>
  );
};
